import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from 'designSystem/lib/utils'
import { Slot } from 'radix-ui'
import * as React from 'react'

const boxVariants = cva('', {
  variants: {
    display: {
      block: 'block',
      flex: 'flex',
      inlineFlex: 'inline-flex',
      grid: 'grid',
      inlineGrid: 'inline-grid',
      hidden: 'hidden',
    },
    direction: {
      row: 'flex-row',
      rowReverse: 'flex-row-reverse',
      column: 'flex-col',
      columnReverse: 'flex-col-reverse',
    },
    align: {
      start: 'items-start',
      center: 'items-center',
      end: 'items-end',
      stretch: 'items-stretch',
      baseline: 'items-baseline',
    },
    justify: {
      start: 'justify-start',
      center: 'justify-center',
      end: 'justify-end',
      between: 'justify-between',
      around: 'justify-around',
      evenly: 'justify-evenly',
    },
    wrap: {
      nowrap: 'flex-nowrap',
      wrap: 'flex-wrap',
      reverse: 'flex-wrap-reverse',
    },
    gap: {
      none: 'gap-0',
      xs: 'gap-1',
      xsPlus: 'gap-1.5',
      sm: 'gap-2',
      md: 'gap-3',
      lg: 'gap-4',
      xl: 'gap-6',
      '2xl': 'gap-8',
    },
    gridColumns: {
      none: 'grid-cols-none',
      one: 'grid-cols-1',
      two: 'grid-cols-2',
      three: 'grid-cols-3',
      four: 'grid-cols-4',
      autoFit: 'grid-cols-[repeat(auto-fit,minmax(0,1fr))]',
    },
    padding: {
      none: 'p-0',
      xs: 'p-1',
      sm: 'p-2',
      md: 'p-3',
      lg: 'p-4',
      xl: 'p-6',
      '2xl': 'p-8',
      '3xl': 'p-12',
    },
    paddingX: {
      none: 'px-0',
      xs: 'px-1',
      sm: 'px-2',
      md: 'px-3',
      lg: 'px-4',
      xl: 'px-6',
      '2xl': 'px-8',
      '3xl': 'px-12',
    },
    paddingY: {
      none: 'py-0',
      xs: 'py-1',
      sm: 'py-2',
      md: 'py-3',
      lg: 'py-4',
      xl: 'py-6',
      '2xl': 'py-8',
      '3xl': 'py-12',
    },
    paddingTop: {
      none: 'pt-0',
      xs: 'pt-1',
      sm: 'pt-2',
      md: 'pt-3',
      lg: 'pt-4',
      xl: 'pt-6',
      '2xl': 'pt-8',
      '3xl': 'pt-12',
    },
    paddingBottom: {
      none: 'pb-0',
      xs: 'pb-1',
      sm: 'pb-2',
      md: 'pb-3',
      lg: 'pb-4',
      xl: 'pb-6',
      '2xl': 'pb-8',
      '3xl': 'pb-12',
    },
    paddingLeft: {
      none: 'pl-0',
      xs: 'pl-1',
      sm: 'pl-2',
      md: 'pl-3',
      lg: 'pl-4',
      xl: 'pl-6',
      '2xl': 'pl-8',
      '3xl': 'pl-12',
    },
    paddingRight: {
      none: 'pr-0',
      xs: 'pr-1',
      sm: 'pr-2',
      md: 'pr-3',
      lg: 'pr-4',
      xl: 'pr-6',
      '2xl': 'pr-8',
      '3xl': 'pr-12',
    },
    margin: {
      none: 'm-0',
      xs: 'm-1',
      sm: 'm-2',
      md: 'm-3',
      lg: 'm-4',
      xl: 'm-6',
      auto: 'm-auto',
    },
    marginX: {
      none: 'mx-0',
      xs: 'mx-1',
      sm: 'mx-2',
      md: 'mx-3',
      lg: 'mx-4',
      xl: 'mx-6',
      auto: 'mx-auto',
    },
    marginY: {
      none: 'my-0',
      xs: 'my-1',
      sm: 'my-2',
      md: 'my-3',
      lg: 'my-4',
      xl: 'my-6',
      auto: 'my-auto',
    },
    radius: {
      none: 'rounded-none',
      xs: 'rounded-xs',
      sm: 'rounded-sm',
      md: 'rounded-md',
      lg: 'rounded-lg',
      xl: 'rounded-xl',
      search: 'rounded-[0.625rem]',
      full: 'rounded-full',
    },
    surface: {
      none: '',
      background: 'bg-background text-foreground',
      card: 'bg-card text-card-foreground',
      muted: 'bg-muted text-foreground',
      secondary: 'bg-secondary text-secondary-foreground',
      accent: 'bg-accent text-accent-foreground',
      transparent: 'bg-transparent',
    },
    border: {
      none: 'border-0',
      default: 'border border-border',
      input: 'border border-input',
      muted: 'border border-muted',
      top: 'border-t border-border',
      right: 'border-r border-border',
      bottom: 'border-b border-border',
      left: 'border-l border-border',
    },
    shadow: {
      none: 'shadow-none',
      xs: 'shadow-xs',
      sm: 'shadow-sm',
      md: 'shadow-md',
      lg: 'shadow-lg',
    },
    overflow: {
      visible: 'overflow-visible',
      hidden: 'overflow-hidden',
      auto: 'overflow-auto',
      scroll: 'overflow-scroll',
      xAuto: 'overflow-x-auto',
      yAuto: 'overflow-y-auto',
      xHidden: 'overflow-x-hidden',
      yHidden: 'overflow-y-hidden',
    },
    width: {
      auto: 'w-auto',
      full: 'w-full',
      fit: 'w-fit',
      screen: 'w-screen',
    },
    height: {
      auto: 'h-auto',
      full: 'h-full',
      fit: 'h-fit',
      screen: 'h-screen',
    },
    minWidth: {
      none: 'min-w-0',
      full: 'min-w-full',
      fit: 'min-w-fit',
    },
    minHeight: {
      none: 'min-h-0',
      full: 'min-h-full',
      screen: 'min-h-screen',
    },
    flex: {
      none: 'flex-none',
      initial: 'flex-initial',
      auto: 'flex-auto',
      one: 'flex-1',
    },
    grow: {
      true: 'grow',
      false: 'grow-0',
    },
    shrink: {
      true: 'shrink',
      false: 'shrink-0',
    },
    textAlign: {
      left: 'text-left',
      center: 'text-center',
      right: 'text-right',
      justify: 'text-justify',
    },
  },
})

const Box = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'> &
    VariantProps<typeof boxVariants> & {
      asChild?: boolean
    }
>(
  (
    {
      className,
      display,
      direction,
      align,
      justify,
      wrap,
      gap,
      gridColumns,
      padding,
      paddingX,
      paddingY,
      paddingTop,
      paddingBottom,
      paddingLeft,
      paddingRight,
      margin,
      marginX,
      marginY,
      radius,
      surface,
      border,
      shadow,
      overflow,
      width,
      height,
      minWidth,
      minHeight,
      flex,
      grow,
      shrink,
      textAlign,
      asChild = false,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot.Root : 'div'

    return (
      <Comp
        data-slot='box'
        data-display={display}
        data-surface={surface}
        className={cn(
          boxVariants({
            display,
            direction,
            align,
            justify,
            wrap,
            gap,
            gridColumns,
            padding,
            paddingX,
            paddingY,
            paddingTop,
            paddingBottom,
            paddingLeft,
            paddingRight,
            margin,
            marginX,
            marginY,
            radius,
            surface,
            border,
            shadow,
            overflow,
            width,
            height,
            minWidth,
            minHeight,
            flex,
            grow,
            shrink,
            textAlign,
            className,
          })
        )}
        ref={ref}
        {...props}
      />
    )
  }
)
Box.displayName = 'Box'

export { Box, boxVariants }
